<template>
  <div>
    <MarketPlaceItemInfo v-if="bcomMarketPlaceItem" />
    <div v-else>
      <ul class="margin-left-xs">
        <template v-if="isFreeShippingMessageAvailable && !isShippingReturnsFinalOfferCopy">
          <li v-for="(message, index) in freeShippingMessages" :key="message">
            <span>
              {{ message }}
              <a v-if="index === 0" :href="FREE_SHIPPING_LINK"> exclusions &amp; details </a>
            </span>
          </li>
        </template>

        <template v-if="!marketPlaceItemEnabled">
          <!-- TODO: call sanitize before placing the variable in v-html -->
          <li v-if="surchargeMessage" v-html="surchargeMessage"></li>
          <li v-if="shippingMessage && !isBigTicketItemFlag" v-html="shippingMessage"></li>
          <li v-if="specialGiftWrapMessage" v-html="specialGiftWrapMessage"></li>
          <li v-if="giftWrapMessage && !isBigTicketItemFlag" v-html="giftWrapMessage"></li>
        </template>

        <template v-if="isMktPlaceShipMsgsAvailable">
          <li>
            This item qualifies for Free Shipping with minimum purchase!
            <a :href="FREE_SHIPPING_LINK" data-auto="exclusions-details-link"> exclusions &amp; details </a>
          </li>
          <li
            v-for="freeShippingItem in marketPlaceProductShippingMessages"
            :key="freeShippingItem"
            data-testid="free-shipping-item"
            v-html="sanitize(freeShippingItem)"
          ></li>
        </template>
        <!-- TODO: call sanitize before placing the variable in v-html -->
        <li v-if="stateRestrictionMessage" v-html="stateRestrictionMessage"></li>

        <li v-for="note in notes" :key="note" data-testid="note" v-html="getNoteHtml(note)"></li>

        <li v-if="isMcom">
          <ShippingAndReturnsPolicyLinks />
        </li>

        <li v-if="returnConstraintMessage" v-html="returnConstraintMessage"></li>
      </ul>

      <MarketPlaceItemInfo v-if="mcomMarketPlaceItem" class="margin-top-m" />

      <ShippingAndReturnsPolicyLinks v-if="!isMcom" class="margin-top-s" />
    </div>
  </div>
</template>

<script setup>
import { sanitize } from '@vue-util/sanitize';

import { toValue } from 'vue';
import { useBrand } from '~/composables/useBrand';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useExperiments from '~/stores/useExperimentsStore';

import ShippingAndReturnsPolicyLinks from './ShippingAndReturnsPolicyLinks';
import MarketPlaceItemInfo from './MarketPlaceItemInfo';

import { FREE_SHIPPING_LINK } from '~/constants/common';

const {
  notes,
  freeShippingMessages,
  returnConstraintMessage,
  stateRestrictionMessage,
  shippingMessage,
  specialGiftWrapMessage,
  giftWrapMessage,
  surchargeMessage,
  marketPlaceProductShippingMessages,
} = useCommonProductGetters();
const { marketPlaceItemEnabled, isShippingReturnsFinalOfferCopy, isLuxury } = useFeatureEligibility();
const { isBigTicketItemFlag } = useProductFlags();
const { isMcom, isBcom } = useBrand();
const {
  experimentFlags: { isProductHighlightsEnabled },
} = useExperiments();

const isFreeShippingMessageAvailable = isMcom && toValue(freeShippingMessages) && !marketPlaceItemEnabled && !isBigTicketItemFlag && !isLuxury;
const isMktPlaceShipMsgsAvailable = isMcom && !!toValue(marketPlaceProductShippingMessages)?.length;
const bcomMarketPlaceItem = isBcom && marketPlaceItemEnabled;
const mcomMarketPlaceItem = isMcom && marketPlaceItemEnabled;

/**
 * Converts phone numbers in sanitized HTML into clickable tel: links.
 * Assumes the input HTML has already been sanitized.
 */
const noteInputSanitizeOptions = {
  whiteList: {},
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style', 'a'],
};

const phoneLinkSanitizeOptions = {
  whiteList: {
    a: ['href'],
  },

  safeAttrValue(tag, name, value) {
    if (tag === 'a' && name === 'href') {
      return /^tel:\+?\d+$/.test(value) ? value : '';
    }

    return '';
  },
};

const addPhoneLinks = (text) => {
  if (!text) return '';

  return text.replace(/(\+?\d[\d\s().-]{6,}\d)/g, (phone) => {
    const telValue = phone.replace(/[^\d+]/g, '');

    return `<a href="tel:${telValue}">${phone}</a>`;
  });
};

const getNoteHtml = (note) => {
  if (isProductHighlightsEnabled) {
    const cleanText = sanitize(note, noteInputSanitizeOptions);
    const htmlWithPhoneLinks = addPhoneLinks(cleanText);

    return sanitize(htmlWithPhoneLinks, phoneLinkSanitizeOptions);
  }

  return sanitize(note);
};
</script>
